import React from "react";

export type SelectOption = { value: string; label: string };
export type SelectOptionGroup = { label: string; options: SelectOption[] };

// Generic replacement for a native <select> — its closed state can be
// skinned to match the app (see .input-wrap), but the open option list is
// rendered by the OS/browser and can't be restyled at all, so this builds
// a trigger + popover instead from the same .menu-item rows used elsewhere
// (see CustomFurnishingItems.tsx for the original of this pattern).
export function Select({
  value,
  onChange,
  options,
  groups,
  placeholder,
}: {
  value: string;
  onChange: (value: string) => void;
  options?: SelectOption[];
  groups?: SelectOptionGroup[];
  placeholder?: string;
}) {
  const [open, setOpen] = React.useState(false);
  const rootRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!open) return;
    function onDocPointerDown(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDocPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onDocPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const flatOptions = groups ? groups.flatMap((g) => g.options) : options || [];
  const selected = flatOptions.find((o) => o.value === value);

  function pick(v: string) {
    setOpen(false);
    onChange(v);
  }

  return (
    <div ref={rootRef} style={{ position: "relative" }}>
      <button
        type="button"
        className="input-wrap select-trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        {selected ? <span>{selected.label}</span> : <span className="placeholder">{placeholder}</span>}
        <svg className={`select-chevron${open ? " open" : ""}`} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4">
          <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && (
        <div className="select-popover" role="listbox">
          {groups
            ? groups.map((g) => (
                <React.Fragment key={g.label}>
                  <div className="select-group-label">{g.label}</div>
                  {g.options.map((o) => (
                    <div key={o.value} className="menu-item" role="option" aria-selected={o.value === value} onClick={() => pick(o.value)}>
                      {o.label}
                    </div>
                  ))}
                </React.Fragment>
              ))
            : (options || []).map((o) => (
                <div key={o.value} className="menu-item" role="option" aria-selected={o.value === value} onClick={() => pick(o.value)}>
                  {o.label}
                </div>
              ))}
        </div>
      )}
    </div>
  );
}
